import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../apiService';
import type { AdminAuditLogEvent } from '../../apiService';
import { formatDateTime } from '../../utils/formatDateTime';

// BAR-135 §5.5: Audit tab. Consumes the existing getAuditLog wrapper (previously
// zero UI consumers) so impersonation_* and update_org_modules rows are
// reviewable from the same panel that starts impersonation. Reverse-chronological
// (the endpoint returns newest-first), cursor-paginated ("Load more"), with a
// client-side action filter over the loaded pages.

interface AuditPanelProps {
  authToken: string;
}

const PAGE_SIZE = 50;

// Known audit actions (auth_service VALID_ACTIONS + the BAR-135 additions).
// Used to populate the filter even before any such row has loaded.
const KNOWN_ACTIONS = [
  'invite_user',
  'resend_invite',
  'disable_user',
  'enable_user',
  'change_role',
  'update_user_profile',
  'delete_user',
  'create_org',
  'update_org',
  'update_org_modules',
  'impersonation_start',
  'impersonation_end',
  'impersonation_revoked',
];

const ACTION_LABELS: Record<string, string> = {
  invite_user: 'Invite user',
  resend_invite: 'Resend invite',
  disable_user: 'Disable user',
  enable_user: 'Enable user',
  change_role: 'Change role',
  update_user_profile: 'Update profile',
  delete_user: 'Delete user',
  create_org: 'Create org',
  update_org: 'Update org',
  update_org_modules: 'Update org modules',
  impersonation_start: 'Impersonation start',
  impersonation_end: 'Impersonation end',
  impersonation_revoked: 'Impersonation revoked',
};

function actionLabel(action?: string | null): string {
  if (!action) return '—';
  return ACTION_LABELS[action] || action.replace(/_/g, ' ');
}

function isImpersonationAction(action?: string | null): boolean {
  return !!action && action.startsWith('impersonation_');
}

function formatTime(ts?: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  // Use the shared locale-aware formatter (UX standard: no direct toLocaleString).
  return formatDateTime(ts, { context: 'log' });
}

function summarizeMetadata(meta?: Record<string, any>): string {
  if (!meta || typeof meta !== 'object') return '';
  const keys = Object.keys(meta);
  if (keys.length === 0) return '';
  try {
    return keys
      .map((k) => {
        const v = meta[k];
        const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
        return `${k}: ${val}`;
      })
      .join(', ');
  } catch {
    return '';
  }
}

export default function AuditPanel({ authToken }: AuditPanelProps) {
  const [events, setEvents] = useState<AdminAuditLogEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<string>('all');

  const loadPage = useCallback(
    async (nextCursor: string | null, append: boolean) => {
      if (append) setIsLoadingMore(true);
      else setIsLoading(true);
      setError(null);
      try {
        const result = await api.getAuditLog(authToken, {
          cursor: nextCursor || undefined,
          limit: PAGE_SIZE,
        });
        const rows: AdminAuditLogEvent[] = Array.isArray(result?.events) ? result.events : [];
        setEvents((prev) => (append ? [...prev, ...rows] : rows));
        setCursor(result?.next_cursor || null);
        setHasMore(!!result?.next_cursor && rows.length > 0);
      } catch (e: any) {
        setError(e?.message || 'Failed to load audit log');
      } finally {
        if (append) setIsLoadingMore(false);
        else setIsLoading(false);
      }
    },
    [authToken],
  );

  useEffect(() => {
    loadPage(null, false);
  }, [loadPage]);

  // Filter vocabulary: known actions plus any extras discovered in loaded rows.
  const actionOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const ev of events) {
      if (ev.action) seen.add(ev.action);
    }
    const extras = Array.from(seen).filter((a) => !KNOWN_ACTIONS.includes(a)).sort();
    return [...KNOWN_ACTIONS, ...extras];
  }, [events]);

  const visibleEvents = useMemo(() => {
    if (actionFilter === 'all') return events;
    return events.filter((ev) => ev.action === actionFilter);
  }, [events, actionFilter]);

  if (isLoading) {
    return <p className="text-sm text-fg-secondary">Loading audit log…</p>;
  }
  if (error) {
    return (
      <div className="text-sm text-danger">
        <p>{error}</p>
        <button onClick={() => loadPage(null, false)} className="mt-2 underline">Retry</button>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <label htmlFor="audit-action-filter" className="text-sm text-fg-secondary">
          Filter by action
        </label>
        <select
          id="audit-action-filter"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="rounded border border-border-default px-2 py-1 text-sm bg-surface"
        >
          <option value="all">All actions</option>
          {actionOptions.map((a) => (
            <option key={a} value={a}>
              {actionLabel(a)}
            </option>
          ))}
        </select>
        {actionFilter !== 'all' && (
          <span className="text-xs text-fg-tertiary">
            {visibleEvents.length} of {events.length} loaded rows
          </span>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border border-border-default">
        <table className="min-w-full divide-y divide-border-default">
          <thead className="bg-bg-secondary">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider whitespace-nowrap">Time</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Action</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Actor</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Target</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Details</th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border-default">
            {visibleEvents.map((ev) => (
              <tr key={ev.id}>
                <td className="px-4 py-3 text-sm text-fg-secondary whitespace-nowrap">
                  {formatTime(ev.timestamp)}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs ' +
                      (isImpersonationAction(ev.action)
                        ? 'bg-warning-bg text-warning'
                        : 'bg-bg-secondary text-fg-secondary border border-border-default')
                    }
                  >
                    {actionLabel(ev.action)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-fg whitespace-nowrap">
                  {ev.actor_email || ev.actor_uid || '—'}
                </td>
                <td className="px-4 py-3 text-sm text-fg whitespace-nowrap">
                  {ev.target_email || ev.target_uid || '—'}
                </td>
                <td className="px-4 py-3 text-xs text-fg-tertiary max-w-md truncate" title={summarizeMetadata(ev.metadata)}>
                  {summarizeMetadata(ev.metadata) || '—'}
                </td>
              </tr>
            ))}
            {visibleEvents.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-fg-secondary">
                  {events.length === 0
                    ? 'No audit events yet.'
                    : 'No events match this filter on the loaded pages. Load more to search further back.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => loadPage(cursor, true)}
            disabled={isLoadingMore}
            className="px-4 py-2 text-sm rounded-md border border-border-default text-fg hover:bg-bg-secondary disabled:opacity-50"
          >
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </>
  );
}
